import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classnames from 'classnames'
import Path from '@leverege/path'
import { Popup, Button, Text, Label } from '@leverege/ui-elements'
import { ButtonGroup } from 'react-bootstrap'

import { Screen, LabelGroup, Flex } from '~/views'
import style from './style.css'

const qs = require( 'querystring' )
const { Constants : ReduxConstants } = require( '@leverege/api-redux' )

const { QueryFactory, util : QueryUtil } = require( '../../editors/query' )
const { ResultsFactory } = require( '../../editors/historyResults' )
const { exportAndSaveCSV, exportAndSaveJson } = require( '../../utils/Export' )

class QueryScreen extends React.Component {

  static resultsDisplayerIconClasses = {
    table : 'fa fa-table',
    blob : 'fa fa-cloud',
    map : 'fa fa-map'
  };

  static qEditorIconClasses = {
    simpleTimeEditor : 'fa fa-cog',
    advancedTimeEditor : 'fa fa-cogs',
    jsonEditor : 'fa fa-code'
  }

  static propTypes = {
    resultsDisplayers : PropTypes.oneOfType( [ PropTypes.arrayOf( PropTypes.string ), PropTypes.func ] ),
    queryEditors : PropTypes.oneOfType( [ PropTypes.arrayOf( PropTypes.string ), PropTypes.func ] ),
    history : PropTypes.object.isRequired,
    location : PropTypes.object.isRequired,
    actions : PropTypes.object.isRequired,
    imagineHistory : PropTypes.object,
    events : PropTypes.object
  };

  static defaultProps = {
    resultsDisplayers : [ 'table', 'blob', 'map' ],
    queryEditors : [ 'simpleTimeEditor', 'advancedTimeEditor', 'jsonEditor' ],
    imagineHistory : {},
    events : {}
  };

  constructor( props, context ) {
    super( props, context )

    const { resultsDisplayers, queryEditors, location } = props

    let search = {}
    if ( location.search ) {
      search = qs.parse( location.search.slice( 1 ) )
    }
    const viewer = search.viewer || ( typeof resultsDisplayers === 'function' ? resultsDisplayers( this.getQuery(), this.getResults() )[0] : resultsDisplayers[0] )
    const editor = search.editor || ( typeof queryEditors === 'function' ? queryEditors( this.getQuery(), this.getResults() )[0] : queryEditors[0] )

    this.state = {
      pagination : {
        startIndex : 0,
        perPage : 100
      },
      qEditor : {
        type : editor
      },
      resultsDisplay : {
        type : viewer
      },
      popupOpen : false
    }
  }

  getQuery( d ) {
    const { queryName, actions, ...rest } = this.props

    return Path( [ actions.reduxKey, 'queries', queryName ] ).get( rest, d )
  }

  getResults( d ) {
    const { queryName, actions, ...rest } = this.props
    return Path( [ actions.reduxKey, 'results', queryName ] ).get( rest, d )
  }

  getItems( d ) {
    const { queryName, actions, ...rest } = this.props
    return Path( [ actions.reduxKey, 'results', queryName, 'items' ] ).get( rest, d )
  }

  componentDidMount() {
    const { pagination } = this.state
    const { dispatch, location, queryable, queryName, queryType, actions } = this.props

    let query = {
      start : Date.now() - 1000 * 60 * 60 * 24,
      end : Date.now(),
      type : queryType,
      order : [
        [ '$time', 'DESC' ]
      ]
    }

    if ( location.search ) {
      const search = qs.parse( location.search.slice( 1 ) )
      if ( search.q ) {
        try {
          query = JSON.parse( search.q )
        } catch ( ex ) {
          // ignore
        }
      }
    }

    dispatch( actions.addQuery( {
      name : queryName,
      query
    } ) )
    
    this.performSearch( { queryName, pagination, queryable } )
  }

  shouldReload( results ) {
    return results && !results.lastQueried && results.status === ReduxConstants.QUERY_STATUS_SUCCESS
  }

  componentDidUpdate( prevProps, prevState ) {
    const { pagination, queryEditor, resultsDisplay } = this.state
    const { queryName, queryable } = this.props

    if ( this.shouldReload( this.getResults() ) || prevState.pagination !== pagination ) {
      this.performSearch( { queryName, pagination, queryable } )
    }
  }

  onEvent = async ( item, event ) => {
    const { pagination } = this.state

    if ( item.type === 'loadMore' && event === 'select' ) {
      const results = this.getResults( {} )
      const startIndex = (
        ( results.ranges && results.ranges[0] && results.ranges[0][1] ) ||
        ( pagination.startIndex + pagination.perPage )
      )
      const newPage = {
        ...pagination,
        startIndex
      }
      await this.performSearch( {
        pagination : newPage
      } )
      this.setState( { pagination : newPage } )
    }
  }

  handlePerPage = ( value ) => {
    this.setState( state => ( { pagination : { ...state.pagination, perPage : value } } ) )
  }

  performSearch( override ) {
    const { dispatch, queryable, queryName, actions } = this.props
    return dispatch( actions.query( { queryable, queryName, ...override } ) )
  }

  onChange = ( { path, value } ) => {
    const rootKey = path.split( '/' )[0]
    const nState = Path( path ).copy( this.state, value )
    this.setState( { [rootKey] : nState[rootKey] } )
  }

  onEditor = ( { data } ) => {
    const { history } = this.props

    this.setState( { qEditor : { type : data.type } } )
    history.replace( this.syncKeyToLocation( 'editor', data.type ) )
  }

  onViewer = ( { data } ) => {
    const { history } = this.props

    this.setState( { resultsDisplay : { type : data.type } } )
    history.replace( this.syncKeyToLocation( 'viewer', data.type ) )
  }

  removeDollar( path ) {
    if ( path[0] === '$' ) {
      return path.slice( 1 )
    }
    return path
  }

  getExportColumns = () => {
    const { blueprint } = this.props
    const { resultsDisplay : { type } } = this.state
    const query = this.getQuery()

    let cols = null
    if ( query.paths && query.paths.length > 0 && type === 'table' ) {
      cols = query.paths.forEach( ( path ) => {
        if ( typeof path === 'string' ) {
          return { path : this.removeDollar( path ).split( '/' ).slice( -1 )[0] }
        }
        return { path : path[1] }
      } )
    } else if ( blueprint && blueprint.data && blueprint.data.attributes && type === 'table' ) {
      cols = blueprint.data.attributes.map( ( att ) => {
        return { path : `data/${att.path}` }
      } )
      cols.unshift( { path : 'time' }, { path : 'id' }, { path : 'userId' } )
    }
    return cols
  }

  onExportCsv = () => {

    const results = this.getItems()
    const cols = this.getExportColumns()

    exportAndSaveCSV( cols, results )
  }

  onExportJson = () => {
    
    const results = this.getItems()
    const cols = this.getExportColumns()

    exportAndSaveJson( cols, results )
  }

  onPopupChange = ( { isOpen } ) => {
    if ( isOpen != null ) {
      this.setState( { popupOpen : isOpen } )
    }
  }

  renderResultsPicker() {
    const { resultsDisplayers } = this.props
    const { resultsDisplay : { type } } = this.state

    let list = resultsDisplayers
    if ( typeof resultsDisplayers === 'function' ) {
      list = resultsDisplayers( this.getQuery(), this.getResults() )
    }

    return (
      <LabelGroup className={style.resultSelector}>
        <Label>Result Viewer:</Label>
        <ButtonGroup className={style.selectorButtons}>
          {
            list.map( displayer => (
              <Button
                variant="null"
                key={displayer}
                eventData={{ type : displayer }}
                className={classnames( style.selector, type === displayer ? style.primary : '' )}
                onClick={this.onViewer}>
                <i className={QueryScreen.resultsDisplayerIconClasses[displayer]} />
              </Button>
            ) )
          }
        </ButtonGroup>
      </LabelGroup>
    )
  }

  renderQueryEditorPicker() {
    const { queryEditors } = this.props
    const { qEditor : { type } } = this.state

    let list = queryEditors
    if ( typeof queryEditors === 'function' ) {
      list = queryEditors( this.getQuery(), this.getResults() )
    }

    return (
      <LabelGroup className={style.editorSelector}>
        <Label>Query Editor:</Label>
        <ButtonGroup className={style.selectorButtons}>
          {
            list.map( editor => (
              <Button
                key={editor}
                variant="null"
                eventData={{ type : editor }}
                className={classnames( style.selector, type === editor ? style.primary : '' )}
                onClick={this.onEditor}>
                <i className={QueryScreen.qEditorIconClasses[editor]} />
              </Button>
            ) )
          }
        </ButtonGroup>
      </LabelGroup>
    )
  }

  syncKeyToLocation( key, value ) {
    const { location } = this.props

    const { search } = location

    let searchObj = { [key] : value }
    if ( search ) {
      searchObj = { ...qs.parse( search.slice( 1 ) ), ...searchObj }
    }
    return `${location.pathname}?${qs.stringify( searchObj )}`
  }

  syncQueryToLocation = ( query ) => {
    const { history } = this.props
    history.replace( this.syncKeyToLocation( 'q', JSON.stringify( query ) ) )
  }

  render() {
    const { blueprint, history, location, queryName, mapSeriesKey, actions, title } = this.props
    const { popupOpen } = this.state

    const results = this.getResults()
    const query = this.getQuery()

    if ( !query ) {
      return null
    }

    const { pagination, qEditor, resultsDisplay } = this.state

    const queryEditor = QueryFactory(
      qEditor.type, 
      query, 
      { 
        onChange : this.onChange, 
        syncWithLocation : this.syncQueryToLocation, 
        history, 
        location, 
        pathSuggestions : blueprint && blueprint.data && blueprint.data.attributes, 
        pagination, 
        queryName, 
        actions,
        className : style.queryEditor 
      } 
    )
    const resultsDisplayer = ResultsFactory( 
      resultsDisplay.type, 
      results, 
      { 
        key : `displayer-${resultsDisplay.type}`, 
        blueprint, 
        query, 
        onEvent : this.onEvent, 
        pageSize : pagination.perPage, 
        actions,
        seriesKey : mapSeriesKey 
      } 
    )

    return (
      <Screen className={style.screen}>
        <Screen.Header className={style.header}>
          <Text variant="screenTitle">{title || 'Device History'}</Text>
        </Screen.Header>
        <Screen.Content className={style.content}>
          <Flex className={style.cFlex} direction="column">
            <div className={style.selectors}>
              {this.renderQueryEditorPicker()}
              {this.renderResultsPicker()}
              <Popup onChange={this.onPopupChange} className={classnames( style.optionsMenu, popupOpen ? style.popupOpen : '' )} title={<i className="fa fa-fw fa-ellipsis-v" />}>
                <Popup.Item className={style.option} onClick={this.onExportCsv}>Export CSV</Popup.Item>
                <Popup.Item className={style.option} onClick={this.onExportJson}>Export Json</Popup.Item>
              </Popup>
            </div>
            {queryEditor}
            {resultsDisplayer}
          </Flex>
        </Screen.Content>
      </Screen>
    )
  }
}

export default connect()( QueryScreen )
